import { IconArrowRight, IconSearch } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { Badge, ErrorState, LoadingState } from '../components/States'
import { PAYMENT_STATUSES, type PaymentStatus, type StatusTotal } from '../lib/apiTypes'
import { daysAgoJakarta, formatCount, formatDateWib, formatRupiah, todayJakarta } from '../lib/format'
import { useDashboard, useMerchants } from '../lib/hooks'
import { paymentStatusLabel, paymentStatusTone } from '../lib/status'

/**
 * Dashboard: pekerjaan yang menunggu operator dulu, baru angka per status.
 *
 * Rentang tanggal bawaan 7 hari terakhir di Asia/Jakarta. Angka yang menunjuk
 * ke daftar membawa filternya, jadi satu klik langsung membuka baris yang
 * dimaksud.
 */
export function DashboardPage() {
  const [params, setParams] = useSearchParams()
  const [from, setFrom] = useState(() => params.get('from') ?? daysAgoJakarta(6))
  const [to, setTo] = useState(() => params.get('to') ?? todayJakarta())
  const [merchant, setMerchant] = useState(() => params.get('merchant') ?? '')

  const query = useMemo(
    () => ({ from, to, merchant: merchant || undefined }),
    [from, to, merchant],
  )

  const dashboard = useDashboard(query)
  const merchants = useMerchants()

  function applyFilters(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next = new URLSearchParams()
    if (from) next.set('from', from)
    if (to) next.set('to', to)
    if (merchant) next.set('merchant', merchant)
    setParams(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Dashboard</h1>
          <p className="muted">
            Ringkasan transaksi pada rentang tanggal yang dipilih, ditambah pekerjaan
            yang masih menunggu tindakan operator.
          </p>
        </div>
      </div>

      {merchants.isError ? <ErrorState title="Gagal memuat pilihan merchant" error={merchants.error} onRetry={() => void merchants.refetch()} /> : null}
      <form className="card" onSubmit={applyFilters}>
        <div className="toolbar">
          <div className="field">
            <label className="field__label" htmlFor="dash-from">
              Dari tanggal
            </label>
            <input
              id="dash-from"
              className="input"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="dash-to">
              Sampai tanggal
            </label>
            <input
              id="dash-to"
              className="input"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="dash-merchant">
              Merchant
            </label>
            <select
              id="dash-merchant"
              disabled={merchants.isPending || merchants.isError}
              className="select"
              value={merchant}
              onChange={(event) => setMerchant(event.target.value)}
            >
              <option value="">{merchants.isPending ? 'Memuat merchant...' : 'Semua merchant yang diizinkan'}</option>
              {(merchants.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name} ({item.slug})
                </option>
              ))}
            </select>
          </div>
          <div className="toolbar__actions">
            <button type="submit" className="btn btn--primary">
              <IconSearch size={18} stroke={1.75} aria-hidden="true" />
              Terapkan
            </button>
          </div>
        </div>
        <p className="card__hint">
          Tanggal dibaca dalam zona Asia/Jakarta. Angka di bawah mengikuti rentang ini,
          kecuali antrean tinjauan dan webhook yang selalu berisi seluruh pekerjaan
          tertunda.
        </p>
      </form>

      {dashboard.isPending ? (
        <LoadingState what="ringkasan transaksi" />
      ) : dashboard.isError ? (
        <ErrorState
          title="Gagal memuat ringkasan transaksi"
          error={dashboard.error}
          onRetry={() => void dashboard.refetch()}
          retrying={dashboard.isFetching}
          hint="Periksa koneksi ke server admin, lalu coba lagi."
        />
      ) : (
        <>
          <section aria-labelledby="queue-heading">
            <div className="page-head">
              <div className="page-head__text">
                <h2 id="queue-heading">Menunggu tindakan</h2>
                <p className="muted">
                  Tiga angka pertama tidak dibatasi tanggal: semuanya pekerjaan yang
                  belum selesai, kapan pun terjadinya.
                </p>
              </div>
            </div>
            <div className="queue">
              <Link
                className="queue__item"
                to={`/transactions?needs_review=true${
                  merchant ? `&merchant=${encodeURIComponent(merchant)}` : ''
                }`}
              >
                <span
                  className={
                    dashboard.data.needs_review > 0
                      ? 'queue__count queue__count--attention'
                      : 'queue__count'
                  }
                >
                  {formatCount(dashboard.data.needs_review)}
                </span>
                <span className="queue__label">
                  Transaksi perlu tinjauan, misalnya nominal bank tidak cocok
                </span>
                <span className="queue__go">
                  Buka daftar transaksi
                  <IconArrowRight size={16} stroke={1.75} aria-hidden="true" />
                </span>
              </Link>

              <Link className="queue__item" to="/webhooks">
                <span
                  className={
                    dashboard.data.webhook_dead > 0
                      ? 'queue__count queue__count--attention'
                      : 'queue__count'
                  }
                >
                  {formatCount(dashboard.data.webhook_dead)}
                </span>
                <span className="queue__label">
                  Webhook gagal kirim setelah seluruh percobaan ulang
                </span>
                <span className="queue__go">
                  Buka daftar webhook
                  <IconArrowRight size={16} stroke={1.75} aria-hidden="true" />
                </span>
              </Link>

              <Link className="queue__item" to="/inbound-rejections">
                <span
                  className={
                    dashboard.data.inbound_rejected > 0
                      ? 'queue__count queue__count--attention'
                      : 'queue__count'
                  }
                >
                  {formatCount(dashboard.data.inbound_rejected)}
                </span>
                <span className="queue__label">
                  Notifikasi bank ditolak dalam rentang tanggal ini
                </span>
                <span className="queue__go">
                  Buka daftar penolakan
                  <IconArrowRight size={16} stroke={1.75} aria-hidden="true" />
                </span>
              </Link>
            </div>
          </section>

          <section className="card" aria-labelledby="status-heading">
            <div className="card__head">
              <h2 className="card__title" id="status-heading">
                Transaksi per status
              </h2>
              <span className="card__hint">
                {dashboard.data.from && dashboard.data.to
                  ? `${formatDateWib(dashboard.data.from)} sampai ${formatDateWib(dashboard.data.to)}`
                  : ''}
              </span>
            </div>

            <table className="status-table">
              <caption className="visually-hidden">
                Jumlah dan nominal transaksi per status pada rentang tanggal terpilih
              </caption>
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col" className="is-num">
                    Jumlah
                  </th>
                  <th scope="col" className="is-num">
                    Nominal
                  </th>
                </tr>
              </thead>
              <tbody>
                {statusRows(dashboard.data.by_status).map((row) => (
                  <tr key={row.status}>
                    <td>
                      <Badge tone={paymentStatusTone(row.status)}>
                        {paymentStatusLabel(row.status)}
                      </Badge>
                    </td>
                    <td className="is-num">
                      <Link
                        className="status-link"
                        to={`/transactions?status=${row.status}${
                          merchant ? `&merchant=${encodeURIComponent(merchant)}` : ''
                        }${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`}
                      >
                        {formatCount(row.count)}
                      </Link>
                    </td>
                    <td className="is-num">{formatRupiah(row.amount_minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="card__hint">
              Nominal untuk status Lunas dan Dikembalikan memakai nilai yang benar-benar
              dibayar bila bank mengirim nominal berbeda. Jumlah yang bernilai nol tetap
              ditampilkan supaya status yang tidak muncul tidak perlu ditebak.
            </p>
          </section>

          <section className="card" aria-labelledby="pending-heading">
            <div className="card__head">
              <h2 className="card__title" id="pending-heading">
                Webhook menunggu kirim
              </h2>
              <Link to="/webhooks?status=PENDING">Buka daftar</Link>
            </div>
            <p>
              <span className="fact__value--lead num">
                {formatCount(dashboard.data.webhook_pending)}
              </span>
            </p>
            <p className="muted">
              Antrean ini belum termasuk yang berstatus gagal kirim. Delivery dikirim
              ulang dengan jeda yang makin panjang, lalu berhenti di status gagal.
            </p>
          </section>
        </>
      )}
    </>
  )
}

function statusRows(rows: StatusTotal[]): StatusTotal[] {
  const byStatus = rows.reduce<Partial<Record<PaymentStatus, StatusTotal>>>((acc, row) => {
    acc[row.status] = row
    return acc
  }, {})
  return PAYMENT_STATUSES.map(
    (status) => byStatus[status] ?? { status, count: 0, amount_minor: 0 },
  )
}
